import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gargoyle from '../images/gargoyle.jpg';
import morningGlory from '../images/MorningGlory.jpg';
import dandelion from '../images/dandelion.jpg';
import thistle from '../images/ThistleSeed.jpg';
import bittercress from '../images/Bittercress.jpg';
import Redflower from '../images/redFlowerR.jpg';
import greenHouse from '../images/greenhouse.jpg';
import gardenPlot from '../images/gardenPlot1R.jpg';
import gardenMap from '../images/emmaCourtGardenMap.png';
import { useSpring, animated } from 'react-spring';
import { FiShare2 } from 'react-icons/fi';

/* ─── Lightbox ───────────────────────────────────────────────────────────────── */

function Lightbox({ image, onClose }) {
  const animation = useSpring({
    opacity: image ? 1 : 0,
    transform: image ? 'scale(1)' : 'scale(0.97)',
    config: { tension: 300, friction: 20 },
  });

  if (!image) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
      onClick={onClose}
    >
      <animated.div
        style={animation}
        className="relative flex max-h-screen w-full max-w-screen-xl flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 text-3xl font-bold text-white transition hover:text-[#e8b4a0]"
          aria-label="Close"
        >
          &times;
        </button>
        <img
          src={image.url}
          alt={image.description || 'Garden photo'}
          className="max-h-[80vh] max-w-full object-contain"
        />
        {image.description?.trim() && (
          <div className="mt-4 max-w-3xl w-full bg-black/50 px-6 py-3 text-center text-white">
            <p className="text-base">{image.description}</p>
          </div>
        )}
      </animated.div>
    </div>
  );
}

/* ─── Clickable circular image ───────────────────────────────────────────────── */

function FeaturePhoto({ src, alt, caption, onClick, objectPosition = 'center' }) {
  return (
    <button
      type="button"
      className="group relative block w-full overflow-hidden bg-[#d9ddcf] text-left focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fbf8ef]"
      onClick={onClick}
      aria-label={`View photo: ${caption}`}
    >
      <img
        src={src}
        alt={alt}
        style={{ objectPosition }}
        className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] select-none pointer-events-none"
        draggable="false"
      />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent px-5 pb-4 pt-14">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white">{caption}</p>
      </div>
    </button>
  );
}

/* ─── Weed thumbnail ─────────────────────────────────────────────────────────── */

function WeedThumb({ src, caption, onClick }) {
  return (
    <button
      type="button"
      className="group overflow-hidden border border-[#d5cfc0] bg-[#fbf8ef] text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#8e3a2b]"
      onClick={onClick}
      aria-label={`View: ${caption}`}
    >
      <img
        src={src}
        alt={caption}
        className="aspect-[4/3] w-full object-cover transition-transform duration-300 group-hover:scale-[1.04] select-none pointer-events-none"
        draggable="false"
      />
      <p className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#34483a]">
        {caption}
      </p>
    </button>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────────────── */

function getCompostShareUrl() {
  const compostPath = '/about#compost';

  if (typeof window === 'undefined') return compostPath;

  return new URL(compostPath, window.location.origin).href;
}

async function copyTextToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the older copy path below.
    }
  }

  if (typeof document === 'undefined') return false;

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  textArea.style.top = '0';

  document.body.appendChild(textArea);
  textArea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

function isLikelyMobileShareDevice() {
  if (typeof navigator === 'undefined') return false;

  if (navigator.userAgentData?.mobile) return true;

  const userAgent = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod/i.test(userAgent)
    || (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
}

function canUseNativeMobileShare(shareData) {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  if (!isLikelyMobileShareDevice()) return false;

  if (typeof navigator.canShare === 'function') {
    return navigator.canShare(shareData);
  }

  return true;
}

function AboutPage() {
  const [lightboxImage, setLightboxImage] = useState(null);
  const compostRef = useRef(null);
  const [shareStatus, setShareStatus] = useState('idle');

  useEffect(() => {
    if (window.location.hash === '#compost' && compostRef.current) {
      const timer = window.setTimeout(() => {
        compostRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
      }, 120);

      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (shareStatus === 'idle') return undefined;

    const timeout = window.setTimeout(() => {
      setShareStatus('idle');
    }, 2200);

    return () => window.clearTimeout(timeout);
  }, [shareStatus]);

  const open = (url, description) => setLightboxImage({ url, description });
  const close = () => setLightboxImage(null);
  const handleCompostShare = () => {
    const shareUrl = getCompostShareUrl();
    const shareData = {
      title: "Emma's Court Compost Rules",
      text: "Compost rules for Emma's Court Community Garden",
      url: shareUrl,
    };

    if (canUseNativeMobileShare(shareData)) {
      navigator.share(shareData).catch((error) => {
        if (error?.name !== 'AbortError') {
          copyTextToClipboard(shareUrl).then((copied) => {
            setShareStatus(copied ? 'copied' : 'error');
          });
        }
      });
      return;
    }

    copyTextToClipboard(shareUrl).then((copied) => {
      setShareStatus(copied ? 'copied' : 'error');
    });
  };

  return (
    <div className="min-h-screen bg-[#f8f4ea] text-[#18271e]">

      <Lightbox image={lightboxImage} onClose={close} />

      {/* ── Page hero ─────────────────────────────────────────────── */}
      <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-20 lg:px-16">
          <h1 className="font-serif text-5xl font-normal leading-tight text-[#0c351e] sm:text-6xl md:text-7xl">
            About the Garden
          </h1>
          <div className="mt-5 h-px w-24 bg-[#304536]" />
          <p className="font-handwritten mt-5 max-w-xl text-xl leading-snug text-[#243c2c] sm:text-2xl">
            Growing food. Building community. Rooted in our neighborhood.
          </p>
        </div>
      </section>

      {/* ── About text + image ────────────────────────────────────── */}
      <section className="border-b border-[#d8d0be] bg-[#f8f4ea]">
        <div className="mx-auto grid max-w-[92rem] gap-10 px-6 py-14 sm:px-10 md:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] md:items-start md:py-20 lg:px-16">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
              Our story
            </p>
            <h2 className="mt-3 font-serif text-4xl text-[#10351f] sm:text-5xl">
              About Emma's Court Garden
            </h2>
            <div className="mt-6 space-y-5 text-lg leading-8 text-[#445044]">
              <p>
                Emma's Court Community Garden is a welcoming space dedicated to bringing people
                together through the shared love of gardening. Nestled in the heart of Philadelphia,
                Pennsylvania, our garden offers individual plots for growing plants, educational
                workshops, and community events that inspire connection and collaboration. Our
                mission is simple yet impactful: to foster a greener, healthier community where
                both plants and people can flourish.
              </p>
              <p>
                Established in 1990, our story began with a group of determined neighbors and a
                devoted nun from St. Michael's Church. Her name was Emma, and it is in her honor
                that our garden carries its name. For over 30 years, we have been dedicated to
                bringing our community together. Whether you're a seasoned gardener or just
                starting out, we invite you to join us.
              </p>
            </div>
          </div>

          <div className="overflow-hidden border border-[#d3cbbb]">
            <img
              src={gardenPlot}
              alt="Garden beds at Emma's Court"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* ── Programs + Natural Spaces ─────────────────────────────── */}
      <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto grid max-w-[92rem] gap-10 px-6 py-14 sm:px-10 md:py-20 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] lg:items-center lg:px-16">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
              Find your way
            </p>
            <h2 className="mt-3 font-serif text-4xl text-[#10351f] sm:text-5xl">
              Garden Layout
            </h2>
            <p className="mt-6 text-lg leading-8 text-[#445044]">
              Explore the numbered plots and shared spaces that make up Emma's Court, including
              the meadows, berry patch, pond, compost area, propagation bed, herb garden, and
              pollinator garden.
            </p>
            <p className="mt-3 text-sm text-[#70806f]">
              Select the map to view it at full size.
            </p>
          </div>

          <figure className="m-0 w-full max-w-3xl justify-self-center">
            <button
              type="button"
              onClick={() => open(gardenMap, "Detailed map of Emma's Court Community Garden")}
              className="block w-full overflow-hidden border border-[#d3cbbb] bg-white p-2 text-left transition-shadow hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fbf8ef]"
              aria-label="Enlarge the Emma's Court garden layout map"
            >
              <img
                src={gardenMap}
                alt="Map of Emma's Court showing numbered plots and shared garden spaces"
                className="h-auto w-full"
              />
            </button>
            <figcaption className="mt-3 text-sm leading-6 text-[#70806f]">
              Emma's Court Community Garden layout and numbered plots. Map created by{' '}
              <a
                href="https://www.ondebureau.com/emma%27s-court"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-[#4f654f] underline decoration-[#aeb5a7] underline-offset-4 transition-colors hover:text-[#8e3a2b]"
              >
                Onde Bureau
              </a>
              .
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-20 lg:px-16">
          <div className="mb-10 border-b border-[#cfc7b6] pb-5">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
              What we do
            </p>
            <h2 className="mt-2 font-serif text-4xl text-[#10351f] sm:text-5xl">
              Programs &amp; Spaces
            </h2>
          </div>

          <div className="grid gap-8 md:grid-cols-2 md:gap-10">
            {/* Programs */}
            <article className="grid overflow-hidden border border-[#d3cbbb] bg-[#f8f4ea] sm:grid-cols-[minmax(0,0.9fr)_minmax(15rem,1.1fr)] md:grid-cols-1 xl:grid-cols-[minmax(0,0.9fr)_minmax(16rem,1.1fr)]">
              <div className="border-t-4 border-[#a43b29] p-6 sm:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8e3a2b]">
                  Grow &amp; share
                </p>
                <h3 className="mt-3 font-serif text-3xl text-[#10351f]">Programs</h3>
                <p className="mt-4 text-base leading-7 text-[#445044]">
                  We provide fresh produce for local charitable organizations from our designated
                  donation plot, as well as excess from our gardeners' personal plots. Community
                  workshops and educational events are offered throughout the growing season.
                </p>
              </div>
              <div className="min-h-64">
                <FeaturePhoto
                  src={greenHouse}
                  alt="Construction of Zac's Verdant Vault"
                  caption="Zac's Verdant Vault"
                  objectPosition="center 58%"
                  onClick={() => open(greenHouse, "Construction of Zac's Verdant Vault")}
                />
              </div>
            </article>

            {/* Natural Spaces */}
            <article className="grid overflow-hidden border border-[#d3cbbb] bg-[#f8f4ea] sm:grid-cols-[minmax(0,0.9fr)_minmax(15rem,1.1fr)] md:grid-cols-1 xl:grid-cols-[minmax(0,0.9fr)_minmax(16rem,1.1fr)]">
              <div className="border-t-4 border-[#304536] p-6 sm:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#58705c]">
                  Habitat &amp; beauty
                </p>
                <h3 className="mt-3 font-serif text-3xl text-[#10351f]">Natural Spaces</h3>
                <p className="mt-4 text-base leading-7 text-[#445044]">
                  We have several pollinator gardens with a diverse array of plant species including
                  native plants. This provides habitat for local wildlife like birds, butterflies,
                  and bees, while improving the overall beauty of our garden space.
                </p>
              </div>
              <div className="min-h-64">
                <FeaturePhoto
                  src={Redflower}
                  alt="Pollinator garden in full bloom"
                  caption="Pollinator Garden"
                  onClick={() => open(Redflower, 'Pollinator garden in full bloom')}
                />
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ── Compost rules ─────────────────────────────────────────── */}
      <section className="border-b border-[#d8d0be] bg-[#f8f4ea]" ref={compostRef} id="compost">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-20 lg:px-16">

          <div className="mb-8 flex flex-col gap-4 border-b border-[#cfc7b6] pb-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                Community guidelines
              </p>
              <h2 className="mt-2 font-serif text-4xl text-[#10351f] sm:text-5xl">
                Compost Rules
              </h2>
            </div>
            <button
              type="button"
              onClick={handleCompostShare}
              className="inline-flex self-start items-center justify-center gap-2 border border-[#c9bfae] px-4 py-2 text-sm font-semibold text-[#5b695c] transition-colors hover:border-[#8e3a2b] hover:bg-[#f4efe3] hover:text-[#10351f] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#f8f4ea] sm:self-auto"
              aria-label="Share the Emma's Court compost rules"
              title={getCompostShareUrl()}
            >
              <FiShare2 className="h-4 w-4" aria-hidden="true" />
              <span aria-live="polite">
                {shareStatus === 'copied'
                  ? 'Copied URL'
                  : shareStatus === 'error'
                    ? 'Copy Failed'
                    : 'Share'}
              </span>
            </button>
          </div>

          <div className="grid gap-6 lg:grid-cols-[minmax(16rem,0.62fr)_minmax(0,1.38fr)]">
            {/* Mascot */}
            <div className="overflow-hidden border border-[#d3cbbb] bg-[#10351f]">
              <img
                src={gargoyle}
                alt="The garden's compost mascot"
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="p-5 text-[#fbf8ef]">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d6c6a8]">
                  A quick sorting guide
                </p>
                <p className="mt-3 font-serif text-2xl leading-snug">
                  Choose the right material and the right bin to keep the pile healthy.
                </p>
              </div>
            </div>

            {/* Rules */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="border-l-4 border-[#66815e] bg-[#eef0e6] p-5">
                <h3 className="font-serif text-2xl text-[#10351f]">
                  Allowable materials
                </h3>
                <p className="mt-2 text-base leading-7 text-[#445044]">
                  Old vegetables, dead plants, weeds (that have not gone to seed), dead leaves,
                  sticks, dead wood, grass clippings.
                </p>
              </div>

              <div className="border-l-4 border-[#a43b29] bg-[#f3e8df] p-5">
                <h3 className="font-serif text-2xl text-[#10351f]">
                  Prohibited materials
                </h3>
                <p className="mt-2 text-base leading-7 text-[#445044]">
                  Trash, weeds (that have gone to seed), tomato cages, stakes, metal, plastic,
                  construction material, dog poop bags.
                </p>
              </div>

              <div className="border border-[#cad2c2] bg-[#fbf8ef] p-5">
                <h3 className="font-serif text-2xl text-[#10351f]">
                  <span className="mr-2 inline-block h-3 w-3 bg-[#4f7547]" aria-hidden="true" />
                  Green bin
                </h3>
                <p className="mt-2 text-base leading-7 text-[#445044]">
                  Fresh vegetation and generally wetter materials.
                </p>
              </div>

              <div className="border border-[#d2c4ad] bg-[#fbf8ef] p-5">
                <h3 className="font-serif text-2xl text-[#10351f]">
                  <span className="mr-2 inline-block h-3 w-3 bg-[#8b6745]" aria-hidden="true" />
                  Brown bin
                </h3>
                <p className="mt-2 text-base leading-7 text-[#445044]">
                  Dead leaves, sticks, woody material, and other dry materials. Sticks should be
                  cut or broken into smaller pieces.
                </p>
              </div>

              {/* Weeds to watch */}
              <div className="border-t border-[#cfc7b6] pt-6 sm:col-span-2">
                <h3 className="font-serif text-2xl text-[#10351f] sm:text-3xl">
                  Weeds to watch
                </h3>
                <p className="mt-2 text-base leading-7 text-[#445044]">
                  Help us identify and remove these invasive or problematic plants before they go to seed.
                </p>
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    { src: morningGlory, caption: 'Morning Glory' },
                    { src: dandelion, caption: 'Dandelion' },
                    { src: thistle, caption: 'Thistle' },
                    { src: bittercress, caption: 'Bittercress' },
                  ].map(({ src, caption }) => (
                    <WeedThumb
                      key={caption}
                      src={src}
                      caption={caption}
                      onClick={() => open(src, caption)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Gallery invitation ────────────────────────────────────── */}
      <section className="bg-[#f1eadc]">
        <div className="mx-auto grid max-w-[92rem] gap-8 px-6 py-14 sm:px-10 md:grid-cols-[minmax(0,1fr)_auto] md:items-end md:py-16 lg:px-16">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
              In the garden
            </p>
            <h2 className="mt-2 font-serif text-4xl text-[#10351f] sm:text-5xl">
              See What&apos;s Growing
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-[#445044]">
              Explore flowers, vegetables, harvests, garden projects, and everyday moments
              from across Emma&apos;s Court.
            </p>
          </div>
          <Link
            to="/gallery"
            className="inline-flex min-w-56 items-center justify-between gap-8 border border-[#43513f] px-5 py-4 text-base font-medium text-[#10351f] transition-colors hover:bg-[#10351f] hover:text-[#fbf8ef] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#f1eadc]"
          >
            <span>View the full gallery</span>
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </section>

    </div>
  );
}

export default AboutPage;
